import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import './AlertProvider.css';

interface AlertButton {
  text: string;
  style?: 'default' | 'cancel' | 'destructive';
  onPress?: () => void;
}

interface Alert {
  title: string;
  message: string;
  buttons: AlertButton[];
}

interface AlertContextType {
  showAlert: (title: string, message: string, buttons?: AlertButton[]) => void;
  dismissAlert: () => void;
  setNavigating: (navigating: boolean) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlertManager = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlertManager must be used within AlertProvider');
  }
  return context;
};

interface AlertProviderProps {
  children: ReactNode;
}

const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alert, setAlert] = useState<Alert | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);

  const showAlert = useCallback((title: string, message: string, buttons: AlertButton[] = [{ text: 'OK' }]) => {
    setAlert({ title, message, buttons });
  }, []);

  const dismissAlert = useCallback(() => {

    setAlert(null);

    setTimeout(() => {
      setAlert(null);
    }, 0);
  }, []);

  const setNavigating = useCallback((navigating: boolean) => {
    setIsNavigating(navigating);
  }, []);

  const handleButtonPress = (button: AlertButton) => {
    if (button.onPress) {
      button.onPress();
    }
    setAlert(null);
  };

  return (
    <AlertContext.Provider value={{ showAlert, dismissAlert, setNavigating }}>
      {children}
      {alert && (
        <div className="alert-overlay" onClick={() => setAlert(null)}>
          <div className="alert-container" onClick={(e) => e.stopPropagation()}>
            <h2 className="alert-title">{alert.title}</h2>
            <p className="alert-message">{alert.message}</p>
            <div className="alert-buttons">
              {alert.buttons.map((button, index) => (
                <button
                  key={index}
                  className={`alert-button alert-button-${button.style || 'default'}`}
                  onClick={() => handleButtonPress(button)}
                >
                  {button.text}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {isNavigating && (
        <div className="navigating-overlay">
          <div className="navigating-spinner"></div>
        </div>
      )}
    </AlertContext.Provider>
  );
};

export default AlertProvider;

